import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, todayIso } from './dates';
import { must, useDb } from './db';
import {
  type CareMessageDraft,
  type CareThreadDraft,
  type MoodEntry,
  OFFLINE_ACTIONS,
} from './offline';

export type CareKind = Database['public']['Enums']['care_kind'];
export type CareTopic = Database['public']['Enums']['care_topic'];
export type CareStatus = Database['public']['Enums']['care_status'];
export type Mood = Database['public']['Enums']['mood'];

export const CARE_TOPICS: readonly CareTopic[] = [
  'friends',
  'study',
  'family',
  'feelings',
  'other',
];
/** From the brightest to the hardest, as the picker shows them. */
export const MOODS: readonly Mood[] = ['great', 'good', 'okay', 'sad', 'angry', 'anxious'];
/** Moods that are hard to carry: the screens offer «Заспокоїтись» and the psychologist after them. */
export const HARD_MOODS: ReadonlySet<Mood> = new Set<Mood>(['sad', 'angry', 'anxious']);

/**
 * Help lines shown on «Мені погано зараз», free and anonymous. Numbers as published by the
 * operators (checked 25.10.2026); recheck before production.
 */
export const HELP_LINES = [
  { key: 'lifeline', phone: '7333' },
  { key: 'children', phone: '116111' },
  { key: 'childrenLandline', phone: '0800500225' },
  { key: 'emergency', phone: '112' },
] as const;
export type HelpLineKey = (typeof HELP_LINES)[number]['key'];

export type GradeBand = '1-4' | '5-9' | '10-11';

export interface CareThread {
  id: string;
  kind: CareKind;
  topic: CareTopic;
  urgent: boolean;
  gradeBand: GradeBand;
  /** shown only once the student chose to */
  classLabel: string | null;
  authorName: string | null;
  incidentPlace: string | null;
  aboutSelf: boolean | null;
  reportedMessageId: string | null;
  status: CareStatus;
  createdAt: string;
  lastMessageAt: string;
  lastFromStudent: boolean;
  /** a new message from the other side since this side last looked */
  unreadForStudent: boolean;
  unreadForStaff: boolean;
}

export interface CareMessage {
  id: string;
  body: string;
  createdAt: string;
  fromStudent: boolean;
  /** staff only: «Юлія Артемівна» */
  authorName: string | null;
  authorRole: 'psychologist' | 'director' | null;
}

const after = (a: string | null, b: string) => a === null || a < b;

/**
 * The threads the person sees: a student their own, the psychologist all of the school, the
 * director the bullying reports. Newest activity first.
 */
export function useCareThreads(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'threads', schoolId],
    refetchInterval: 20_000,
    queryFn: async (): Promise<CareThread[]> =>
      must(
        await db
          .from('care_threads')
          .select(
            'id, kind, topic, urgent, grade_band, class_label, author_name, incident_place, about_self, reported_message_id, status, created_at, last_message_at, last_from_student, student_read_at, staff_read_at',
          )
          .eq('school_id', schoolId)
          .order('last_message_at', { ascending: false })
          .limit(300),
      ).map((t) => ({
        id: t.id,
        kind: t.kind,
        topic: t.topic,
        urgent: t.urgent,
        // a check constraint keeps the column to these three values
        gradeBand: t.grade_band as GradeBand,
        classLabel: t.class_label,
        authorName: t.author_name,
        incidentPlace: t.incident_place,
        aboutSelf: t.about_self,
        reportedMessageId: t.reported_message_id,
        status: t.status,
        createdAt: t.created_at,
        lastMessageAt: t.last_message_at,
        lastFromStudent: t.last_from_student,
        unreadForStudent: !t.last_from_student && after(t.student_read_at, t.last_message_at),
        unreadForStaff: t.last_from_student && after(t.staff_read_at, t.last_message_at),
      })),
  });
}

/** Messages of one thread, oldest first; refreshed every few seconds while it is open. */
export function useCareMessages(threadId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'messages', threadId],
    enabled: threadId !== undefined,
    refetchInterval: 5000,
    queryFn: async (): Promise<CareMessage[]> =>
      must(
        await db
          .from('care_messages')
          .select(
            'id, body, created_at, from_student, author:school_members!care_messages_school_id_author_member_id_fkey(first_name, middle_name, last_name, roles:staff_roles!staff_roles_school_id_member_id_fkey(role))',
          )
          .eq('thread_id', threadId as string)
          .order('created_at')
          .limit(500),
      ).map((m) => {
        const author = m.author as typeof m.author | null;
        const roles = (author?.roles ?? []).map((r) => r.role);
        return {
          id: m.id,
          body: m.body,
          createdAt: m.created_at,
          fromStudent: m.from_student,
          authorName: author
            ? `${author.first_name} ${author.middle_name ?? author.last_name}`
            : null,
          authorRole: roles.includes('psychologist')
            ? 'psychologist'
            : roles.includes('director')
              ? 'director'
              : null,
        };
      }),
  });
}

/** The chat message a complaint is about (for those who answer it). */
export function useReportedMessage(threadId: string | undefined, enabled: boolean) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'reported', threadId],
    enabled: enabled && threadId !== undefined,
    queryFn: async () =>
      must(await db.rpc('care_reported_message', { p_thread: threadId as string }))[0] ?? null,
  });
}

export function useCareActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['care'] });
  return {
    /** Works without connection: the thread is opened as soon as the app is back online. */
    open: useMutation<string, Error, CareThreadDraft>({ mutationKey: OFFLINE_ACTIONS.careOpen }),
    /** Works without connection; queued messages go out in order. */
    send: useMutation<unknown, Error, CareMessageDraft>({ mutationKey: OFFLINE_ACTIONS.careSend }),
    reveal: useMutation({
      mutationFn: async (a: { threadId: string; showClass: boolean; showName: boolean }) =>
        must(
          await db.rpc('care_reveal', {
            p_thread: a.threadId,
            p_show_class: a.showClass,
            p_show_name: a.showName,
          }),
        ),
      onSettled: invalidate,
    }),
    markRead: useMutation({
      mutationFn: async (threadId: string) =>
        must(await db.rpc('care_mark_read', { p_thread: threadId })),
      onSettled: () => client.invalidateQueries({ queryKey: ['care', 'threads'] }),
    }),
    setStatus: useMutation({
      mutationFn: async (a: { threadId: string; status: CareStatus }) =>
        must(await db.rpc('care_set_status', { p_thread: a.threadId, p_status: a.status })),
      onSettled: invalidate,
    }),
  };
}

// ---------------------------------------------------------------------------------------------
// The mood of the day
// ---------------------------------------------------------------------------------------------

export interface MoodDay {
  day: string;
  mood: Mood;
  note: string | null;
}

/** The student's own moods of the last weeks, newest first (nobody else can read them). */
export function useMoodHistory(studentId: string, days = 28) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'mood', studentId, days],
    queryFn: async (): Promise<MoodDay[]> =>
      must(
        await db
          .from('mood_checkins')
          .select('day, mood, note')
          .eq('student_member_id', studentId)
          .gte('day', addDays(todayIso(), -days))
          .order('day', { ascending: false }),
      ),
  });
}

/** Today's mood in one tap, also offline. */
export function useSetMood() {
  return useMutation<unknown, Error, MoodEntry>({ mutationKey: OFFLINE_ACTIONS.setMood });
}

export interface ClassMood {
  classId: string;
  classLabel: string;
  grade: number;
  respondents: number;
  entries: number;
  counts: Record<Mood, number>;
  /** the share of sad, angry and anxious entries, 0…1 */
  hardShare: number;
}

/** The psychologist: classes where at least 5 students answered in the period — counts only. */
export function useMoodOverview(schoolId: string, from: string, to: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'overview', schoolId, from, to],
    queryFn: async (): Promise<ClassMood[]> =>
      must(await db.rpc('care_mood_overview', { p_school: schoolId, p_from: from, p_to: to })).map(
        (r) => {
          const counts: Record<Mood, number> = {
            great: r.great,
            good: r.good,
            okay: r.okay,
            sad: r.sad,
            angry: r.angry,
            anxious: r.anxious,
          };
          return {
            classId: r.class_id,
            classLabel: r.class_label,
            grade: r.grade,
            respondents: r.respondents,
            entries: r.entries,
            counts,
            hardShare: r.entries === 0 ? 0 : (r.sad + r.angry + r.anxious) / r.entries,
          };
        },
      ),
  });
}

// ---------------------------------------------------------------------------------------------
// Meetings with the psychologist
// ---------------------------------------------------------------------------------------------

/** The student: free future slots of the school's psychologists. */
export function useCareMeetingSlots(schoolId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['care', 'slots', schoolId],
    queryFn: async () => must(await db.rpc('care_meeting_slots', { p_school: schoolId })),
  });
}

export function useBookCareMeeting() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { slotId: string; topic: string }) =>
      must(await db.rpc('book_care_meeting', { p_slot: a.slotId, p_topic: a.topic.trim() })),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['care', 'slots'] });
      void client.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}
